import React from 'react';

import BenchlingConfig from 'BenchlingConfig';
import {TagSchema} from 'constants/RegistryConstants';
import {
  FieldType,
  RequiredLink,
  SchemaField,
  isRequiredLinkCustomSchemaInterface,
  isRequiredLinkStaticSchemaInterface,
} from 'schemas/fields/SchemaFieldConstants';
import QueryingRestrictToSchemaDropdown from 'schemas/schema-interface/typecasting/QueryingRestrictToSchemaDropdown';
import {Schema} from 'schemas/SchemaConstants';
import {isTagSchema} from 'schemas/SchemaPredicates';
import {isLinkField} from 'tag-schema/util/TagSchemaUtil';
import APIUtil from 'util/APIUtil';

import {
  LookupStepInfo,
  LookupStepSpec,
  LookupStepTreeItem,
  LookupStepTreeItemsById,
  LookupStepType,
} from './LookupConfigModalConstants';
import {
  DETAIL_STEP_TYPES,
  getIconForLookupStepInfo,
  getLookupStepInfoWithType,
  getObjectTypesForLookupStepInfo,
  getStepDisplayInfo,
  isStepWithTypecastSchemaMetadata,
} from './LookupConfigUtils';
import {getLookupStepInfoForFieldStepItem} from './LookupStepInitializerUtils';

export function hasFollowingSchemaFieldSteps(
  step: LookupStepTreeItem,
  schemaFieldTreeFilterFn: ((field: SchemaField) => boolean) | undefined
): boolean {
  const lookupStepInfoWithType = getLookupStepInfoWithType(step);
  const parentSchema = lookupStepInfoWithType?.linkSchema;
  if (!parentSchema) {
    return false;
  }
  for (const schemaField of parentSchema.fields) {
    if (shouldShowSchemaFieldInTree(schemaField, schemaFieldTreeFilterFn)) {
      return true;
    }
  }
  return false;
}

function forceAllowSchemaFieldStepsIfTypecastable(lookupStepInfo: LookupStepInfo): boolean {
  if (isStepWithTypecastSchemaMetadata(lookupStepInfo.lookupStep)) {
    const originalParentSchema = lookupStepInfo?.originalLinkSchema;

    // Allow schema field steps to allow for typecast selection even if there are no child fields
    if (originalParentSchema && APIUtil.isSchemaInterface(originalParentSchema.id)) {
      return true;
    }
  }
  return false;
}

export function allowSchemaFieldSteps(
  lookupStepGroup: Array<LookupStepInfo>,
  schemaFieldTreeFilterFn: ((field: SchemaField) => boolean) | undefined
): boolean {
  if (lookupStepGroup.length === 0) {
    return false;
  }

  const firstLookupStepItem = lookupStepGroup[0];
  const {isPlateLayoutStep} = getObjectTypesForLookupStepInfo(firstLookupStepItem);
  if (isPlateLayoutStep) {
    return false;
  }
  const lookupStepInfoWithType = getLookupStepInfoWithType(firstLookupStepItem);
  if (lookupStepInfoWithType && forceAllowSchemaFieldStepsIfTypecastable(lookupStepInfoWithType)) {
    return true;
  }
  const parentSchema = lookupStepInfoWithType?.linkSchema;

  return (
    parentSchema != null &&
    parentSchema.fields.filter((field) => shouldShowSchemaFieldInTree(field, schemaFieldTreeFilterFn))
      .length !== 0
  );
}

export function shouldShowSchemaFieldInTree(
  field: SchemaField,
  schemaFieldTreeFilterFn: ((field: SchemaField) => boolean) | undefined
): boolean {
  return (
    isLinkField(field) &&
    // TODO(BNCH-92584): Leverage does link support lookup config traversal
    !isRequiredLinkStaticSchemaInterface(field.requiredLink) &&
    FieldType.BATCH_LINK !== field.fieldType &&
    FieldType.SELECTOR !== field.fieldType &&
    (schemaFieldTreeFilterFn ? schemaFieldTreeFilterFn(field) : true)
  );
}

export function getFollowingSchemaFieldSteps(
  step: LookupStepTreeItem,
  schemaFieldTreeFilterFn: ((field: SchemaField) => boolean) | undefined,
  typecastRestrictedSchemas: Map<string, Schema | null>,
  setTypecastRestrictedSchema: (nodeId: string, typecastRestrictedSchema: Schema | null) => void,
  initialSchemaFieldToTypecast: Map<string, Schema | null>
): Array<LookupStepTreeItem> {
  const lookupStepInfoWithType = getLookupStepInfoWithType(step);
  const parentField = lookupStepInfoWithType?.schemaField;
  const parentTypecastSchema = getTypecastSchema(
    step.id,
    typecastRestrictedSchemas,
    initialSchemaFieldToTypecast,
    parentField
  );
  const parentSchema = parentTypecastSchema ?? lookupStepInfoWithType?.originalLinkSchema;

  const followingSteps: Array<LookupStepTreeItem> = [];
  const parentId = step.id;
  if (!parentSchema) {
    return followingSteps;
  }
  for (const schemaField of parentSchema.fields) {
    if (!shouldShowSchemaFieldInTree(schemaField, schemaFieldTreeFilterFn)) {
      continue;
    }
    const nodeId = `${parentId}-${LookupStepType.SCHEMA_FIELD}-${schemaField.apiId}`;

    const typecastSchema = getTypecastSchema(
      nodeId,
      typecastRestrictedSchemas,
      initialSchemaFieldToTypecast,
      schemaField
    );

    const schemaFieldStepInfo = getLookupStepInfoForFieldStepItem(
      LookupStepType.SCHEMA_FIELD,
      schemaField,
      parentSchema,
      typecastSchema
    );
    const secondaryLabels = constructSecondaryLabelsForTypecast(
      schemaField.requiredLink,
      nodeId,
      typecastSchema,
      setTypecastRestrictedSchema
    );

    followingSteps.push({
      ...schemaFieldStepInfo,
      id: nodeId,
      parentId,
      icon: getIconForLookupStepInfo(schemaFieldStepInfo),
      depth: step.depth + 1,
      linkSchema: typecastSchema ?? schemaFieldStepInfo.originalLinkSchema ?? null,
      secondaryLabels,
      typecastSchema,
    });
  }
  return followingSteps;
}

/**
 * The RESULTS lookup step takes in a linkedBySchemaField that we select in the lookup form. We intentionally
 * do not want to render this as an option in the following linked item tree since it has already been
 * selected.
 */

function filterResultsLookupStepSchemaFields(field: SchemaField, parentStep: LookupStepInfo): boolean {
  if (parentStep.lookupStep.type === LookupStepType.RESULTS) {
    return parentStep.lookupStep.linkedBySchemaField !== field.apiId;
  }
  return true;
}

function constructSecondaryLabelsForTypecast(
  link: RequiredLink | null | undefined,
  stepId: string,
  typecastedSchema: Schema | null,
  setTypecastRestrictedSchema: (nodeId: string, typecastRestrictedSchema: Schema | null) => void
): Array<JSX.Element> | null {
  if (!BenchlingConfig.SCHEMAS_ENABLE_INTERFACE_TYPECASTING) {
    return null;
  }
  if (
    link &&
    // TODO(BNCH-92584): Leverage does link support lookup config traversal
    isRequiredLinkCustomSchemaInterface(link) &&
    (!typecastedSchema || isTagSchema(typecastedSchema))
  ) {
    return [
      <QueryingRestrictToSchemaDropdown
        schemaInterfaceId={link.schemaInterface.id}
        onSelectSchema={(selectedSchema: TagSchema | null): void => {
          setTypecastRestrictedSchema(stepId, selectedSchema);
        }}
        selectedSchema={typecastedSchema}
      />,
    ];
  }
  return null;
}

function getTypecastSchema(
  nodeId: string,
  localTypecastRestrictedSchemas: Map<string, Schema | null>,
  initialSchemaFieldToTypecast: Map<string, Schema | null>,
  schemaField: SchemaField | null | undefined
): Schema | null {
  // There are two cases to consider when determining the typecast of a node:
  // - the first time we are generating the SchemaFieldSelectTree from the lookup step group. In this case, we
  //   rely on the initialSchemaFieldToTypecast map (derived from the lookup step group info) to determine
  //   desired typecasting
  // - intermediary renders while the user is interacting with the SchemaFieldSelectTree. In this case, we
  //   can rely solely on the localTypecastRestrictedSchemas map, which is local state to the component updated
  //   as dropdown selections are made but not yet persisted to the lookup step group.
  if (localTypecastRestrictedSchemas.size > 0) {
    return localTypecastRestrictedSchemas.get(nodeId) ?? null;
  }
  if (!schemaField) {
    return null;
  }
  return initialSchemaFieldToTypecast.get(schemaField.apiId) ?? null;
}

export function getSchemaFieldSelectTreeItems(
  parentStep: LookupStepInfo,
  schemaFieldTreeFilterFn: ((field: SchemaField) => boolean) | undefined,
  numLevelsToFetch: number,
  finalDepth: number,
  typecastRestrictedSchemas: Map<string, Schema | null>,
  setTypecastRestrictedSchema: (nodeId: string, typecastRestrictedSchema: Schema | null) => void,
  initialSchemaFieldToTypecast: Map<string, Schema | null>,
  rootStepIdOverride?: string, // should never be ROOT
  startingDepth: number = 0
): LookupStepTreeItemsById {
  const rootStepId = rootStepIdOverride || 'ROOT';
  const parentTypecastSchema = getTypecastSchema(
    rootStepId,
    typecastRestrictedSchemas,
    initialSchemaFieldToTypecast,
    parentStep.schemaField
  );
  const parentSecondaryLabel = constructSecondaryLabelsForTypecast(
    parentStep.schemaField?.requiredLink,
    rootStepId,
    parentTypecastSchema,
    setTypecastRestrictedSchema
  );

  const lookupStepWithInjectedTypecast: LookupStepSpec = {
    ...parentStep.lookupStep,
  };
  if (isStepWithTypecastSchemaMetadata(lookupStepWithInjectedTypecast)) {
    lookupStepWithInjectedTypecast.typecastSchema = parentTypecastSchema?.id ?? null;
  }

  const lookupStepInfoWithType = getLookupStepInfoWithType(parentStep);
  let parentSchema: Schema | null;
  if (lookupStepInfoWithType && isStepWithTypecastSchemaMetadata(lookupStepInfoWithType.lookupStep)) {
    // If the parent step has typecast metadata, but the typecast is explicitly null, we wish to use the originalLinkSchema.
    // We fall under this case when generating the new SchemaSelectTree after clearing out a typecast selection.
    // As we are curretly generating the tree, lookupStepInfoWithType.linkSchema will be the previous typecasted schema,
    // which we do not want to use.
    parentSchema = parentTypecastSchema ?? lookupStepInfoWithType?.originalLinkSchema ?? null;
  } else {
    parentSchema = parentTypecastSchema ?? lookupStepInfoWithType?.linkSchema ?? null;
  }

  const parentStepWithInjectedTypecast: LookupStepInfo = {
    ...parentStep,
    lookupStep: lookupStepWithInjectedTypecast,
    linkSchema: parentSchema,
    typecastSchema: parentTypecastSchema,
  };
  const parentStepName = rootStepIdOverride
    ? parentStepWithInjectedTypecast.name
    : getStepDisplayInfo(parentStepWithInjectedTypecast).name;
  parentStepWithInjectedTypecast.name = parentStepName;
  const currentTree: LookupStepTreeItemsById = {
    [rootStepId]: {
      ...parentStepWithInjectedTypecast,
      id: rootStepId,
      parentId: null,
      icon: getIconForLookupStepInfo(parentStep),
      depth: startingDepth,
      secondaryLabels: parentSecondaryLabel,
    },
  };

  if (parentSchema == null) {
    return currentTree;
  }
  let currLayerSteps: Array<LookupStepTreeItem> = parentSchema.fields
    .filter((field) => shouldShowSchemaFieldInTree(field, schemaFieldTreeFilterFn))
    .filter((field) => filterResultsLookupStepSchemaFields(field, parentStep))
    .map((field) => {
      const nodeId = `${rootStepIdOverride ? `${rootStepIdOverride}-` : ''}${LookupStepType.SCHEMA_FIELD}-${
        field.apiId
      }`;
      const typecastSchema = getTypecastSchema(
        nodeId,
        typecastRestrictedSchemas,
        initialSchemaFieldToTypecast,
        field
      );
      const schemaFieldStepInfo = getLookupStepInfoForFieldStepItem(
        LookupStepType.SCHEMA_FIELD,
        field,
        parentSchema,
        typecastSchema
      );
      const secondaryLabels = constructSecondaryLabelsForTypecast(
        field.requiredLink,
        nodeId,
        typecastSchema,
        setTypecastRestrictedSchema
      );

      return {
        ...schemaFieldStepInfo,
        id: `${rootStepIdOverride ? `${rootStepIdOverride}-` : ''}${LookupStepType.SCHEMA_FIELD}-${
          field.apiId
        }`,
        parentId: rootStepId,
        icon: getIconForLookupStepInfo(schemaFieldStepInfo),
        depth: startingDepth + 1,
        linkSchema: typecastSchema || schemaFieldStepInfo.originalLinkSchema || null,
        secondaryLabels,
        typecastSchema,
      };
    });

  numLevelsToFetch = Math.min(finalDepth - startingDepth, numLevelsToFetch);

  if (numLevelsToFetch) {
    for (const step of currLayerSteps) {
      currentTree[step.id] = step;
    }
  }
  for (let i = 1; i < numLevelsToFetch; i++) {
    const prevLayerSteps = [...currLayerSteps];
    currLayerSteps = [];
    for (const step of prevLayerSteps) {
      currLayerSteps = [
        ...currLayerSteps,
        ...getFollowingSchemaFieldSteps(
          step,
          schemaFieldTreeFilterFn,
          typecastRestrictedSchemas,
          setTypecastRestrictedSchema,
          initialSchemaFieldToTypecast
        ),
      ];
    }
    for (const step of currLayerSteps) {
      currentTree[step.id] = step;
    }
  }
  return currentTree;
}

export function getLookupStepTreeItemId(lookupStep: LookupStepSpec): string {
  return lookupStep.type === LookupStepType.SCHEMA_FIELD
    ? `${lookupStep.type}-${lookupStep.schemaField}`
    : lookupStep.type;
}

export function getAllParentSteps(
  firstItem: LookupStepTreeItem | null,
  itemsById: LookupStepTreeItemsById
): Array<LookupStepTreeItem> {
  let selectTreeItem = firstItem;
  const parentSteps: Array<LookupStepTreeItem> = [];
  while (selectTreeItem) {
    parentSteps.push(selectTreeItem);
    selectTreeItem = selectTreeItem.parentId ? itemsById[selectTreeItem.parentId] : null;
  }
  return parentSteps;
}

export function isDetailStep(
  lookupStepInfo: LookupStepInfo,
  schemaFieldTreeFilterFn: ((field: SchemaField) => boolean) | undefined,
  isLastStep: boolean,
  isDetailStepRequired?: boolean
): boolean {
  if (isDetailStepRequired && isLastStep) {
    return true;
  }
  if (lookupStepInfo.lookupStep.type === LookupStepType.SCHEMA_FIELD && lookupStepInfo.schemaField) {
    // If the step is the last step and it's a schema field lookup step, we should just force it to be a detail step.
    if (isLastStep) {
      return true;
    }
    return !shouldShowSchemaFieldInTree(lookupStepInfo.schemaField, schemaFieldTreeFilterFn);
  }
  return DETAIL_STEP_TYPES.includes(lookupStepInfo.lookupStep.type);
}
